import type { UploadConfig } from '../types/upload';

interface AppConfig {
  api: {
    baseUrl: string;
    timeout: number;
  };
  media: UploadConfig;
  rateLimit: {
    retryEnabled: boolean;
    maxRetries: number;
  };
  polling: {
    scanStatusIntervalMs: number;
    scanStatusMaxPolls: number;
  };
  features: {
    offlineMode: boolean;
    debugMode: boolean;
  };
  sentry?: {
    dsn: string;
    environment: string;
  };
}

function getEnv(key: string, defaultValue?: string): string {
  const value = import.meta.env[key];
  if (value === undefined) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

function getEnvNumber(key: string, defaultValue?: number): number {
  const value = import.meta.env[key];
  if (value === undefined) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Missing required environment variable: ${key}`);
  }
  const parsed = Number(value);
  if (isNaN(parsed)) {
    throw new Error(`Invalid number for environment variable ${key}: ${value}`);
  }
  return parsed;
}

function getEnvBoolean(key: string, defaultValue: boolean): boolean {
  const value = import.meta.env[key];
  if (value === undefined) {
    return defaultValue;
  }
  return value === 'true' || value === '1';
}

function getEnvArray(key: string, defaultValue: string[]): string[] {
  const value = import.meta.env[key];
  if (!value) {
    return defaultValue;
  }
  return value.split(',').map((s: string) => s.trim()).filter(Boolean);
}

export const config: AppConfig = {
  api: {
    baseUrl: getEnv('VITE_API_BASE_URL', ''),
    timeout: getEnvNumber('VITE_API_TIMEOUT', 30000),
  },
  media: {
    maxSingleUploadMB: getEnvNumber('VITE_MEDIA_MAX_SINGLE_UPLOAD_MB', 10),
    multipartPartSizeMB: getEnvNumber('VITE_MEDIA_MULTIPART_PART_SIZE_MB', 8),
    allowedMimeTypes: getEnvArray('VITE_MEDIA_ALLOWED_MIMES', [
      // Images - all common mobile and desktop formats
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/heic',
      'image/heif',
      'image/avif',
      'image/tiff',
      'image/bmp',
      'image/svg+xml',
      // Videos - iPhone, Android, desktop
      'video/mp4',
      'video/quicktime',
      'video/webm',
      'video/x-msvideo',
      'video/x-matroska',
      'video/3gpp',
      // Audio - comprehensive mobile support
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/wave',
      'audio/x-wav',
      'audio/aac',
      'audio/mp4',
      'audio/x-m4a',
      'audio/m4a',
      'audio/flac',
      'audio/x-flac',
      'audio/ogg',
      'audio/opus',
      'audio/aiff',
      'audio/x-aiff',
      'audio/x-ms-wma',
      'audio/x-alac',
      'audio/amr',
      'audio/x-amr',
    ]),
    presignedExpiryMinutes: getEnvNumber('VITE_MEDIA_PRESIGNED_EXPIRY_MINUTES', 15),
    maxRetries: getEnvNumber('VITE_UPLOAD_MAX_RETRIES', 3),
    retryDelayMs: getEnvNumber('VITE_UPLOAD_RETRY_DELAY_MS', 1000),
    concurrentParts: getEnvNumber('VITE_UPLOAD_CONCURRENT_PARTS', 4),
  },
  rateLimit: {
    // Disable retry in development to prevent UI flickering
    retryEnabled: getEnvBoolean('VITE_RATE_LIMIT_RETRY_ENABLED', !import.meta.env.DEV),
    maxRetries: getEnvNumber('VITE_RATE_LIMIT_MAX_RETRIES', 3),
  },
  polling: {
    scanStatusIntervalMs: getEnvNumber('VITE_SCAN_STATUS_POLL_INTERVAL_MS', 3000),
    scanStatusMaxPolls: getEnvNumber('VITE_SCAN_STATUS_MAX_POLLS', 40),
  },
  features: {
    offlineMode: getEnvBoolean('VITE_FEATURE_OFFLINE_MODE', true),
    debugMode: getEnvBoolean('VITE_FEATURE_DEBUG_MODE', false),
  },
};

// Optional Sentry config
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  config.sentry = {
    dsn: sentryDsn,
    environment: getEnv('VITE_SENTRY_ENVIRONMENT', 'development'),
  };
}

// Validate configuration on load
if (config.media.maxSingleUploadMB <= 0) {
  throw new Error('VITE_MEDIA_MAX_SINGLE_UPLOAD_MB must be greater than 0');
}

if (config.media.multipartPartSizeMB <= 0) {
  throw new Error('VITE_MEDIA_MULTIPART_PART_SIZE_MB must be greater than 0');
}

if (config.media.allowedMimeTypes.length === 0) {
  throw new Error('VITE_MEDIA_ALLOWED_MIMES must contain at least one MIME type');
}

if (import.meta.env.DEV) {
  console.log('[Config] Loaded configuration:', config);
}
